import bs58 from 'bs58';

export interface OwnershipSignatureProof {
  signature: Uint8Array;
  signedMessage?: Uint8Array;
}

function decodeHex(value: string): Uint8Array | undefined {
  const hex = value.startsWith('0x') ? value.slice(2) : value;
  if (!/^[0-9a-fA-F]+$/.test(hex) || hex.length % 2 !== 0) return undefined;
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function coerceBytes(value: unknown): Uint8Array | undefined {
  if (value instanceof Uint8Array) return value;
  if (Array.isArray(value)) return Uint8Array.from(value);
  if (typeof value !== 'string') return undefined;

  try {
    return bs58.decode(value);
  } catch {
    try {
      return decodeHex(value);
    } catch {
      try {
        if (typeof atob === 'function') {
          const binary = atob(value);
          return Uint8Array.from(binary, (char) => char.charCodeAt(0));
        }
      } catch {
        return undefined;
      }
    }
  }

  return undefined;
}

function splitOffchainEnvelope(bytes: Uint8Array): OwnershipSignatureProof | null {
  if (bytes.length <= 65 || bytes[0] < 1 || bytes[0] > 8) {
    return null;
  }

  const signatureCount = bytes[0];
  const signatureEnd = 1 + signatureCount * 64;
  if (bytes.length <= signatureEnd) {
    return null;
  }

  return {
    signature: bytes.slice(1, 65),
    signedMessage: bytes,
  };
}

function normalizeBareSignature(bytes: Uint8Array): Uint8Array {
  if (bytes.length === 64) return bytes;
  if (bytes.length > 64 && bytes[0] === 1) {
    return bytes.slice(1, 65);
  }
  if (bytes.length > 64) {
    return bytes.slice(0, 64);
  }
  throw new Error('Wallet returned an invalid signature');
}

function readSignatureFields(record: Record<string, unknown>) {
  const signatureValue =
    record.signature ?? record.sig ?? record.data ?? record.result ?? record.sign;
  const signedMessageValue =
    record.signedMessage ?? record.signed_message ?? record.signed_message_bytes;

  return {
    signatureBytes: coerceBytes(signatureValue),
    signedMessageBytes: coerceBytes(signedMessageValue),
  };
}

export function parseWalletSignMessageResult(raw: unknown): OwnershipSignatureProof {
  if (raw instanceof Uint8Array) {
    const envelope = splitOffchainEnvelope(raw);
    if (envelope) return envelope;
    return { signature: normalizeBareSignature(raw) };
  }

  if (typeof raw === 'string') {
    const bytes = coerceBytes(raw);
    if (!bytes) throw new Error('Wallet returned an invalid signature');
    const envelope = splitOffchainEnvelope(bytes);
    if (envelope) return envelope;
    return { signature: normalizeBareSignature(bytes) };
  }

  if (typeof raw === 'object' && raw !== null) {
    const record = raw as Record<string, unknown>;
    const { signatureBytes, signedMessageBytes } = readSignatureFields(record);

    if (signatureBytes) {
      const envelope = splitOffchainEnvelope(signatureBytes);
      if (envelope) {
        return {
          signature: envelope.signature,
          signedMessage: signedMessageBytes ?? envelope.signedMessage,
        };
      }

      return {
        signature: normalizeBareSignature(signatureBytes),
        signedMessage: signedMessageBytes,
      };
    }

    if (signedMessageBytes) {
      const envelope = splitOffchainEnvelope(signedMessageBytes);
      if (envelope) return envelope;
    }
  }

  throw new Error('Wallet returned an invalid signature');
}
